import { IconValue } from "@gettactic/components";
import { Switch } from "@gettactic/components/src/primitives/Switch";
import { getEnumKeys } from "@gettactic/helpers";
import { getCssVariableValue } from "@gettactic/helpers/src/css/getCssVariableValue";
import { capitalize } from "@gettactic/helpers/src/strings/capitalize";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Copy } from "lucide-react";
import React from "react";
import toast from "react-hot-toast";
import { useCustomParkingVerbiageOnMapBuilder as useCustomParkingVerbiage } from "../../../../../hooks/useCustomParkingVerbiageOnMapBuilder";
import {
  SpotAppearance,
  SpotLabelOrientation,
  SpotLabelPlacement,
  SpotOrientation,
  SpotShape,
  SpotShapeShape,
  SpotShapeType,
  SpotShapeTypeToLabel,
  SpotStateArea,
  SpotStateIcons
} from "../../../../../types";
import { detectRoomRectForSpot } from "../../../../../utils/detectRoomRect";
import { getEffectiveSpotAppearance } from "../../../../../utils/spots";
import { computeStateIconCenterOffset } from "../../../../../utils/stateIconCentering";
import { ColorPickerInput } from "../../../../ui/ColorPickerInput";
import { EditorFieldGrid, EditorFieldRow } from "../../../../ui/EditorField";
import { EditorSelect } from "../../../../ui/EditorSelect";
import { IconButton } from "../../../../ui/IconButton";
import { PanelSection } from "../../../../ui/PanelShell";
import { Seg } from "../../../../ui/Seg";
import { useMapCanvasShapes } from "../../../contexts/MapCanvasContext";
import {
  useMapEditorActions,
  useMapEditorTool,
  useMapEditorUI
} from "../../../contexts/MapEditorContext";
import {
  DEFAULT_NODE_CIRCLE_RADIUS,
  DEFAULT_NODE_LABEL_ALIGNMENT
} from "../../../defines";
import { DragNumberInput } from "../../controls/ScrollDragNumberInput";
import { ShapeSectionOptions } from "../ShapePanel/ShapePanel";
import { SpotStateIconControl } from "./SpotStateIconControl";
import { SpotStateIconPreview } from "./SpotStateIconPreview";

const DEFAULT_STATE_AREA_RECT = {
  x: -20,
  y: -20,
  width: 130,
  height: 90,
  cornerRadius: 8
};

export interface SharedSpotPanelControlsProps extends ShapeSectionOptions {
  isSpotGroup?: boolean;
}

export const SharedSpotPanelControls: React.FC<
  SharedSpotPanelControlsProps
> = ({ shapes, isSpotGroup }) => {
  const shape = shapes[0] as SpotShape;
  const { updateShape, setActiveTool } = useMapEditorActions();
  const { highlightedShape } = useMapEditorUI();
  const { activeTool } = useMapEditorTool();
  const { shapes: canvasShapes } = useMapCanvasShapes();
  const isDrawingStateArea = activeTool === "state-area";

  const selectedItem = React.useMemo(() => {
    return shape;
  }, [shape, highlightedShape?.targetId, isSpotGroup]);

  // Self-heal legacy data: a parking-type spot's Appearance dropdown no
  // longer offers "Parking"/"Parking Space" as options, and the render
  // below shows "Default" for one even if spotAppearance is still stale —
  // but that's a display-only fallback, it never writes the correction. A
  // stale value never actually changes on its own (re-selecting "Default"
  // from the dropdown is a no-op once it's already showing that), so fix
  // the underlying data directly instead of only papering over the display.
  // Silent (allowHistoryUpdate=false): merely selecting a legacy spot must
  // not count as a user edit — it shouldn't dirty the canvas or push undo
  // history, or an undo would restore the stale value only for this same
  // effect to immediately "fix" it again, fighting the user's undo.
  React.useEffect(() => {
    for (const s of shapes) {
      const spot = s as SpotShape;
      const isParkingType =
        spot.spotType === "parking_lot" || spot.spotType === "parking_space";
      const hasStaleParkingAppearance =
        spot.spotAppearance === "parking_lot" ||
        spot.spotAppearance === "parking_space";
      if (isParkingType && hasStaleParkingAppearance) {
        updateShape(spot.id, { spotAppearance: SpotShapeType.Node }, false);
      }
    }
  }, [shapes, updateShape]);

  if (!isSpotGroup && !highlightedShape) return;
  if (!selectedItem) return;

  const handleOrientationChange = (orientation: SpotOrientation) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { orientation });
    });
  };

  const handleLabelPlacementChange = (labelPlacement: SpotLabelPlacement) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { labelPlacement });
    });
  };

  const handleLabelOrientationChange = (
    labelOrientation: SpotLabelOrientation
  ) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { labelOrientation });
    });
  };

  const _handleSpacingChangeValue = (spacing: number | string) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, {
        spacing: Number.parseInt(String(spacing), 10)
      });
    });
  };

  const _handleRowSpacingChangeValue = (rowSpacing: number | string) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, {
        rowSpacing: Number.parseInt(String(rowSpacing), 10)
      });
    });
  };

  const _handleLabelTextAlignChange = (
    textAlign: "center" | "left" | "right"
  ) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { textAlign });
    });
  };

  const handleActive = (isActive: boolean) => {
    shapes.forEach((shape) => {
      updateShape(shape.id, { isInactive: !isActive });
    });
  };

  const startDrawingStateArea = () => {
    // Clear any previous polygon so the draw starts fresh; the canvas tool
    // appends corners to the selected spot's stateArea on each click.
    shapes.forEach((current) => {
      const currentArea = (current as SpotShape).stateArea;
      updateShape(current.id, {
        stateArea: {
          kind: "polygon",
          hideDot: currentArea?.hideDot,
          points: []
        }
      });
    });
    setActiveTool("state-area");
  };

  const handleStateAreaKindChange = (
    kind: SpotStateArea["kind"] | "none"
  ) => {
    if (kind === "polygon") {
      startDrawingStateArea();
      return;
    }
    shapes.forEach((current) => {
      const currentArea = (current as SpotShape).stateArea;
      if (kind === "none") {
        updateShape(current.id, { stateArea: undefined });
      } else if (kind === "spot-rect") {
        updateShape(current.id, {
          stateArea: { kind: "spot-rect", hideDot: currentArea?.hideDot }
        });
      } else {
        updateShape(current.id, {
          stateArea: {
            kind: "rect",
            hideDot: currentArea?.hideDot,
            rect: currentArea?.rect || DEFAULT_STATE_AREA_RECT
          }
        });
      }
    });
  };

  const handleStateAreaRectChange = (
    patch: Partial<NonNullable<SpotStateArea["rect"]>>
  ) => {
    const currentRect = shape.stateArea?.rect || DEFAULT_STATE_AREA_RECT;
    updateShape(shape.id, {
      stateArea: {
        kind: "rect",
        hideDot: shape.stateArea?.hideDot,
        rect: { ...currentRect, ...patch }
      }
    });
  };

  const handleDetectRoom = () => {
    shapes.forEach((current) => {
      const spot = current as SpotShape;
      const roomRect = detectRoomRectForSpot(canvasShapes, spot);
      if (!roomRect) {
        toast.error("No wall-enclosed room found around this spot");
        return;
      }
      updateShape(spot.id, {
        stateArea: {
          kind: "rect",
          hideDot: spot.stateArea?.hideDot,
          rect: roomRect
        }
      });
    });
  };

  const handleStateAreaShowDotChange = (isDotShown: boolean) => {
    shapes.forEach((current) => {
      const currentArea = (current as SpotShape).stateArea;
      if (!currentArea) return;
      updateShape(current.id, {
        stateArea: { ...currentArea, hideDot: !isDotShown }
      });
    });
  };

  const handleStateAreaOccupiedColorChange = (
    occupiedColor: string | undefined
  ) => {
    shapes.forEach((current) => {
      const currentArea = (current as SpotShape).stateArea;
      if (!currentArea) return;
      updateShape(current.id, {
        stateArea: { ...currentArea, occupiedColor }
      });
    });
  };

  const handleStateIconChange = (
    state: "occupied" | "available",
    icon: IconValue | undefined
  ) => {
    shapes.forEach((current) => {
      updateShape(current.id, {
        stateIcons: {
          ...(current as SpotShape).stateIcons,
          [state]: icon
        }
      });
    });
  };

  // Position/size/color are shared between the occupied and available
  // icons — only one of them ever renders at a time, so a single style
  // keeps the icon from jumping around (or changing color) as availability
  // changes. `occupiedBackgroundColor` is intentionally occupied-only — it
  // colors the small backdrop behind the icon specifically when booked.
  const handleStateIconStyleChange = (
    patch: Partial<
      Pick<
        SpotStateIcons,
        "offsetX" | "offsetY" | "size" | "color" | "occupiedBackgroundColor"
      >
    >
  ) => {
    shapes.forEach((current) => {
      updateShape(current.id, {
        stateIcons: {
          ...(current as SpotShape).stateIcons,
          ...patch
        }
      });
    });
  };

  // Each selected spot has its own area, so its offset is computed
  // individually per shape rather than sharing one patch across the
  // selection like handleStateIconStyleChange does.
  const handleCenterIconOnArea = () => {
    shapes.forEach((current) => {
      const spot = current as SpotShape;
      const offset = computeStateIconCenterOffset(spot);
      if (!offset) return;
      updateShape(spot.id, {
        stateIcons: {
          ...spot.stateIcons,
          offsetX: Math.round(offset.offsetX),
          offsetY: Math.round(offset.offsetY)
        }
      });
    });
  };

  const handleLabelRotationChangeValue = (rotation: number) => {
    return updateShape(shape.id, {
      textProps: {
        ...shape.textProps,
        rotation: Number.parseInt(String(rotation), 10)
      }
    });
  };

  const handleLabelWidthChangeValue = (width: number) => {
    return updateShape(shape.id, {
      textProps: {
        ...shape.textProps,
        width: Number.parseInt(String(width), 10)
      }
    });
  };

  const handleLabelMarginTopChangeValue = (marginTop: number) => {
    return updateShape(shape.id, {
      textProps: {
        ...shape.textProps,
        marginTop: Number.parseInt(String(marginTop), 10)
      }
    });
  };

  const handleLabelMarginLeftChangeValue = (marginLeft: number) => {
    return updateShape(shape.id, {
      textProps: {
        ...shape.textProps,
        marginLeft: Number.parseInt(String(marginLeft), 10)
      }
    });
  };

  const handleSpotShapeChangeValue = (spotShape: SpotShapeShape) => {
    return updateShape(shape.id, {
      spotShape
    });
  };

  const handleSpotTypeChange = (
    spotType: SpotAppearance | "default",
    isAppearance = false
  ) => {
    shapes.forEach((shape) => {
      if (isAppearance) {
        const shapeSpotType = (shape as SpotShape).spotType;
        const shapeIsParking =
          shapeSpotType === "parking_lot" || shapeSpotType === "parking_space";
        updateShape(shape.id, {
          spotAppearance:
            spotType === "default"
              ? // "Default" falls back to the shape's own spotType
                // (spotAppearance || spotType) — which for a parking spot
                // IS the parking look. Explicitly pin it to Node instead so
                // "Default" really means "look like a real Node spot" here,
                // matching the same default applied on type change.
                shapeIsParking
                ? SpotShapeType.Node
                : undefined
              : spotType
        });
        return;
      }
      if (spotType !== "default" && spotType !== "circle") {
        const isParkingType =
          spotType === "parking_lot" || spotType === "parking_space";
        updateShape(shape.id, {
          spotType: spotType as SpotShapeType,
          // A parking spot's own type already IS parking — appearance
          // "Default" would otherwise fall back to that same parking look
          // (spotAppearance || spotType), which reads redundant/oversized
          // for a single spot. Default it to the plain Node dot instead.
          spotAppearance: isParkingType ? SpotShapeType.Node : undefined,
          // A state area from whatever this spot was before (e.g. a room's
          // highlight rect, possibly with its dot hidden) would otherwise
          // survive the conversion and keep overriding the plain Node dot
          // regardless of appearance — clear it so parking really renders
          // exactly like a real Node spot by default.
          ...(isParkingType ? { stateArea: undefined } : {})
        });
      }
    });

    if (
      spotType === "meeting_room" ||
      spotType === "circle" ||
      spotType === "parking_lot" ||
      spotType === "parking_space" ||
      spotType === "workspace"
    ) {
      handleLabelPlacementChange("outside");
    }

    if (spotType === "meeting_room" || spotType === "circle") {
      handleLabelOrientationChange("top");
    }

    if (spotType === "desk") {
      handleLabelOrientationChange("auto");
      handleLabelPlacementChange("on-rect");
    }
  };

  const labelPlacementValue = shape.labelPlacement;
  let labelOrientation = shape.labelOrientation || "auto";
  const _labelTextAlign = shape.textAlign || DEFAULT_NODE_LABEL_ALIGNMENT;

  const isLabelForcedToAuto =
    shape.labelPlacement === "hidden" ||
    (shape.orientation !== "left" && shape.orientation !== "right");

  if (isLabelForcedToAuto) {
    labelOrientation = "auto";
  }

  const shapeAppearance = getEffectiveSpotAppearance(
    shape.spotType,
    shape.spotAppearance
  );
  const isRoomAppearance =
    shapeAppearance === "meeting_room" || shapeAppearance === "circle";
  const isLabelPlacementDisabled = isRoomAppearance;
  const isLabelInsideDisabled = !(shapeAppearance === "desk");
  const isLabelAutoDisabled = isRoomAppearance;

  const circleRadius = shape.circleProps?.radius || DEFAULT_NODE_CIRCLE_RADIUS;
  const hasStateIcon = !!(
    shape.stateIcons?.occupied || shape.stateIcons?.available
  );
  // Matches InteractiveZoneSVG/InteractiveZone: when neither offset has been
  // customized AND a highlight area is configured, the real render
  // auto-centers the icon on that area instead of the dot — without this,
  // the mini preview here would show the pre-auto-center (dot) position.
  const hasCustomIconOffset =
    shape.stateIcons?.offsetX !== undefined ||
    shape.stateIcons?.offsetY !== undefined;
  const autoCenterIconOffset = !hasCustomIconOffset
    ? computeStateIconCenterOffset(shape)
    : null;
  const previewIconOffsetX =
    shape.stateIcons?.offsetX ?? autoCenterIconOffset?.offsetX ?? 0;
  const previewIconOffsetY =
    shape.stateIcons?.offsetY ?? autoCenterIconOffset?.offsetY ?? 0;
  // Preview swatch for the color picker when no custom occupied color is
  // set — matches the fallback the viewer actually paints (SpotShapeItemSVG).
  const defaultOccupiedHighlightColor =
    getCssVariableValue("--map-spot-not-bookeable-unavailable-fill") ||
    "#e8ecef";
  // Neutral swatch for the icon color picker when unset — the real
  // computed default varies with availability state (InteractiveZoneSVG),
  // so this is just a reasonable starting point, not a live preview of it.
  const defaultStateIconColor = "#374151";

  // biome-ignore lint/correctness/useHookAtTopLevel: pre-existing early returns above; hoisting these hooks is a behavior-risk refactor out of scope for the UI pass
  const customParkingVerbiage = useCustomParkingVerbiage();

  const isDeskAppearance =
    (selectedItem.spotType === "desk" &&
      (selectedItem.spotAppearance === "desk" ||
        selectedItem.spotAppearance === undefined)) ||
    (selectedItem.spotType !== "desk" &&
      selectedItem.spotAppearance === "desk");

  // A parking spot already renders as parking by virtue of its own type —
  // letting its Appearance be set to "Parking"/"Parking Space" is a no-op
  // (rendering falls back to spotType || spotAppearance either way) and
  // reads as a redundant, confusing choice. Appearance overrides exist for
  // making OTHER spot types look like parking, not for parking itself.
  const spotTypeIsParking =
    selectedItem.spotType === "parking_lot" ||
    selectedItem.spotType === "parking_space";

  // biome-ignore lint/correctness/useHookAtTopLevel: see note above
  const spotTitle = React.useMemo(() => {
    if (shape.type !== "spot") return "";

    const spotShape = shape as SpotShape;

    const spotIsParking =
      spotShape.spotType === "parking_lot" ||
      spotShape.spotType === "parking_space";

    let title = capitalize(SpotShapeTypeToLabel[spotShape.spotType]);
    if (spotIsParking && customParkingVerbiage !== null) {
      title = `${capitalize(customParkingVerbiage)} Group`;
    }
    return title;
  }, [shape.type, customParkingVerbiage, shape]);

  return (
    <>
      <PanelSection label="Resource">
        <div className="flex flex-col gap-3">
          {selectedItem.resource_id ? (
            <div className="flex min-h-7 items-center gap-2">
              <span className="flex-none text-neutral-600 text-xs">ID</span>
              <span
                title={selectedItem.resource_id}
                className="block min-w-0 flex-1 truncate text-right font-mono text-[10.5px] text-neutral-400"
              >
                {selectedItem.resource_id}
              </span>
              <IconButton
                size="sm"
                label="Copy ID"
                onClick={() => {
                  navigator.clipboard.writeText(selectedItem.resource_id ?? "");
                  toast.success("Resource ID copied");
                }}
              >
                <Copy size={13} />
              </IconButton>
            </div>
          ) : null}
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Type</span>
            <Seg<SpotShapeType>
              label="Type"
              value={selectedItem.spotType}
              onValueChange={(value) => {
                if (value === selectedItem.spotType) return;
                handleSpotTypeChange(value);
              }}
              options={getEnumKeys(SpotShapeType)
                .filter((type) => type !== "Node")
                .map((type) => ({
                  value: SpotShapeType[type],
                  label:
                    type === "Parking"
                      ? capitalize(customParkingVerbiage ?? "Parking")
                      : type === "ParkingSpace"
                        ? `${capitalize(customParkingVerbiage ?? "Parking")} Space`
                        : type
                }))}
            />
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Appearance</span>
            <EditorSelect
              label="Appearance"
              value={
                // A parking-type spot's own appearance override is always
                // redundant (see spotTypeIsParking above) — fall back to
                // "Default" even if legacy data left a stale parking value
                // set, since that value is no longer a selectable option.
                spotTypeIsParking &&
                (selectedItem.spotAppearance === "parking_lot" ||
                  selectedItem.spotAppearance === "parking_space")
                  ? "default"
                  : selectedItem.spotAppearance || "default"
              }
              onValueChange={(value) => {
                if (value === (selectedItem.spotAppearance || "default"))
                  return;
                handleSpotTypeChange(value as SpotAppearance | "default", true);
              }}
              options={[
                { value: "default", label: "Default" },
                ...getEnumKeys(SpotShapeType)
                  .filter(
                    (type) =>
                      !spotTypeIsParking ||
                      (type !== "Parking" && type !== "ParkingSpace")
                  )
                  .map((type) => ({
                    value: SpotShapeType[type],
                    label:
                      type === "Parking"
                        ? capitalize(customParkingVerbiage ?? "Parking")
                        : type
                  })),
                { value: "circle", label: "Circle" }
              ]}
            />
          </div>
          {isDeskAppearance ? (
            <div className="flex flex-col gap-1">
              <span className="text-neutral-600 text-xs">Desk Shape</span>
              <EditorSelect
                label="Desk Shape"
                value={selectedItem.spotShape || "default"}
                onValueChange={(value) => {
                  handleSpotShapeChangeValue(value as SpotShapeShape);
                }}
                options={[{ value: "default", label: "Default" }]}
              />
            </div>
          ) : undefined}
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">
              {spotTitle} Orientation
            </span>
            <Seg<SpotOrientation | "">
              label={`${spotTitle} Orientation`}
              className={
                isLabelPlacementDisabled
                  ? "pointer-events-none opacity-50"
                  : undefined
              }
              value={isLabelPlacementDisabled ? "" : selectedItem.orientation}
              onValueChange={(value) => {
                if (isLabelPlacementDisabled) return;
                if (!value || value === selectedItem.orientation) return;
                handleOrientationChange(value);
              }}
              options={[
                {
                  value: "top",
                  ariaLabel: "Top",
                  label: <ArrowUp className="h-3.5 w-3.5" />
                },
                {
                  value: "bottom",
                  ariaLabel: "Bottom",
                  label: <ArrowDown className="h-3.5 w-3.5" />
                },
                {
                  value: "left",
                  ariaLabel: "Left",
                  label: <ArrowLeft className="h-3.5 w-3.5" />
                },
                {
                  value: "right",
                  ariaLabel: "Right",
                  label: <ArrowRight className="h-3.5 w-3.5" />
                }
              ]}
            />
          </div>
          <EditorFieldRow label="Active">
            <Switch
              checked={!shape.isInactive}
              onCheckedChange={handleActive}
            />
          </EditorFieldRow>
        </div>
      </PanelSection>
      <PanelSection label="Availability States">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Highlight Area</span>
            <Seg<SpotStateArea["kind"] | "none">
              label="Highlight Area"
              value={shape.stateArea?.kind || "none"}
              onValueChange={(value) => {
                if (value === (shape.stateArea?.kind || "none")) return;
                handleStateAreaKindChange(value);
              }}
              options={[
                { value: "none", ariaLabel: "None", label: "None" },
                { value: "spot-rect", ariaLabel: "Room", label: "Room" },
                { value: "rect", ariaLabel: "Custom", label: "Custom" },
                { value: "polygon", ariaLabel: "Drawn", label: "Drawn" }
              ]}
            />
            {isDrawingStateArea ? (
              <p className="rounded-md bg-blue-50 px-2 py-1.5 text-blue-700 text-xs">
                Click the map to add corners. Double-click, right-click or
                click the first corner to finish.
              </p>
            ) : (
              <>
                {shape.stateArea?.kind === "polygon" ? (
                  <button
                    type="button"
                    className="w-full rounded-md bg-blue-50 px-2 py-1.5 text-blue-700 text-xs hover:bg-blue-100"
                    onClick={startDrawingStateArea}
                  >
                    Redraw Area
                  </button>
                ) : null}
                <button
                  type="button"
                  className="w-full rounded-md bg-blue-50 px-2 py-1.5 text-blue-700 text-xs hover:bg-blue-100"
                  onClick={handleDetectRoom}
                  title="Fill the area from the walls enclosing this spot"
                >
                  Detect Room from Walls
                </button>
              </>
            )}
          </div>
          {shape.stateArea ? (
            <EditorFieldRow label="Show Dot">
              <Switch
                checked={!shape.stateArea.hideDot}
                onCheckedChange={handleStateAreaShowDotChange}
              />
            </EditorFieldRow>
          ) : null}
          {shape.stateArea ? (
            <EditorFieldRow label="Occupied Area Color">
              <ColorPickerInput
                value={
                  shape.stateArea.occupiedColor || defaultOccupiedHighlightColor
                }
                onChange={handleStateAreaOccupiedColorChange}
              />
              {shape.stateArea.occupiedColor ? (
                <button
                  type="button"
                  className="text-blue-600 text-xs hover:text-blue-800"
                  onClick={() => handleStateAreaOccupiedColorChange(undefined)}
                >
                  Reset
                </button>
              ) : null}
            </EditorFieldRow>
          ) : null}
          {shape.stateArea?.kind === "rect" ? (
            <>
              <EditorFieldGrid>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">X</span>
                  <DragNumberInput
                    size={"sm"}
                    value={shape.stateArea.rect?.x ?? 0}
                    onChange={(x) =>
                      handleStateAreaRectChange({ x: Number(x) })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">Y</span>
                  <DragNumberInput
                    size={"sm"}
                    value={shape.stateArea.rect?.y ?? 0}
                    onChange={(y) =>
                      handleStateAreaRectChange({ y: Number(y) })
                    }
                  />
                </div>
              </EditorFieldGrid>
              <EditorFieldGrid>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">Width</span>
                  <DragNumberInput
                    size={"sm"}
                    min={1}
                    value={shape.stateArea.rect?.width ?? 0}
                    onChange={(width) =>
                      handleStateAreaRectChange({ width: Number(width) })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">Height</span>
                  <DragNumberInput
                    size={"sm"}
                    min={1}
                    value={shape.stateArea.rect?.height ?? 0}
                    onChange={(height) =>
                      handleStateAreaRectChange({ height: Number(height) })
                    }
                  />
                </div>
              </EditorFieldGrid>
              <EditorFieldGrid>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">Radius</span>
                  <DragNumberInput
                    size={"sm"}
                    min={0}
                    value={shape.stateArea.rect?.cornerRadius ?? 0}
                    onChange={(cornerRadius) =>
                      handleStateAreaRectChange({
                        cornerRadius: Number(cornerRadius)
                      })
                    }
                  />
                </div>
              </EditorFieldGrid>
            </>
          ) : null}
          <SpotStateIconControl
            label="Occupied Icon"
            value={shape.stateIcons?.occupied}
            onChange={(icon) => handleStateIconChange("occupied", icon)}
          />
          <SpotStateIconControl
            label="Available Icon"
            value={shape.stateIcons?.available}
            onChange={(icon) => handleStateIconChange("available", icon)}
          />
          {hasStateIcon ? (
            <>
              <SpotStateIconPreview
                occupiedIcon={shape.stateIcons?.occupied}
                availableIcon={shape.stateIcons?.available}
                offsetX={previewIconOffsetX}
                offsetY={previewIconOffsetY}
                size={shape.stateIcons?.size ?? circleRadius * 2}
                circleRadius={circleRadius}
                color={shape.stateIcons?.color}
                occupiedBackgroundColor={
                  shape.stateIcons?.occupiedBackgroundColor
                }
              />
              {shape.stateArea ? (
                <button
                  type="button"
                  className="w-full rounded-md bg-blue-50 px-2 py-1.5 text-blue-700 text-xs hover:bg-blue-100"
                  onClick={handleCenterIconOnArea}
                  title="Position the icon at the center of the highlight area"
                >
                  Center on Area
                </button>
              ) : null}
              <EditorFieldGrid>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">Icon X</span>
                  <DragNumberInput
                    size={"sm"}
                    value={shape.stateIcons?.offsetX ?? 0}
                    onChange={(offsetX) =>
                      handleStateIconStyleChange({ offsetX: Number(offsetX) })
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">Icon Y</span>
                  <DragNumberInput
                    size={"sm"}
                    value={shape.stateIcons?.offsetY ?? 0}
                    onChange={(offsetY) =>
                      handleStateIconStyleChange({ offsetY: Number(offsetY) })
                    }
                  />
                </div>
              </EditorFieldGrid>
              <EditorFieldGrid>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">Icon Size</span>
                  <DragNumberInput
                    size={"sm"}
                    min={1}
                    value={shape.stateIcons?.size ?? circleRadius * 2}
                    onChange={(size) =>
                      handleStateIconStyleChange({ size: Number(size) })
                    }
                  />
                </div>
              </EditorFieldGrid>
              <EditorFieldRow label="Icon Color">
                <ColorPickerInput
                  value={shape.stateIcons?.color || defaultStateIconColor}
                  onChange={(color) => handleStateIconStyleChange({ color })}
                />
                {shape.stateIcons?.color ? (
                  <button
                    type="button"
                    className="text-blue-600 text-xs hover:text-blue-800"
                    onClick={() =>
                      handleStateIconStyleChange({ color: undefined })
                    }
                  >
                    Reset
                  </button>
                ) : null}
              </EditorFieldRow>
              <EditorFieldRow label="Occupied Icon Background">
                <ColorPickerInput
                  value={
                    shape.stateIcons?.occupiedBackgroundColor || "#ffffff00"
                  }
                  onChange={(occupiedBackgroundColor) =>
                    handleStateIconStyleChange({ occupiedBackgroundColor })
                  }
                />
                {shape.stateIcons?.occupiedBackgroundColor ? (
                  <button
                    type="button"
                    className="text-blue-600 text-xs hover:text-blue-800"
                    onClick={() =>
                      handleStateIconStyleChange({
                        occupiedBackgroundColor: undefined
                      })
                    }
                  >
                    Reset
                  </button>
                ) : null}
              </EditorFieldRow>
              {shape.stateIcons?.occupiedBackgroundColor ? (
                <p className="text-[10.5px] text-neutral-400">
                  On the live map this only shows while the resource is actually
                  booked/occupied. There's no real occupied state while editing,
                  so the previews above always show it behind the Occupied Icon
                  — that's expected, not a bug.
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      </PanelSection>
      <PanelSection label="Label">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Label Type</span>
            <Seg<SpotLabelPlacement>
              label="Label Type"
              value={labelPlacementValue}
              onValueChange={(value) => {
                if (value === "on-rect" && isLabelInsideDisabled) return;
                if (value === labelPlacementValue) return;
                handleLabelPlacementChange(value);
              }}
              options={[
                {
                  value: "on-rect",
                  ariaLabel: "Top",
                  label: (
                    <span
                      className={
                        isLabelInsideDisabled ? "opacity-40" : undefined
                      }
                    >
                      Inside
                    </span>
                  )
                },
                {
                  value: "outside-node",
                  ariaLabel: "Outside Resource",
                  label: "Outside"
                },
                { value: "hidden", ariaLabel: "Hidden", label: "Hidden" }
                /* The "outside" and "custom" placements are hidden for now,
                   but the functionality is still there */
              ]}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Label Position</span>
            <Seg<SpotLabelOrientation>
              label="Label Position"
              className={
                isLabelForcedToAuto
                  ? "pointer-events-none opacity-50"
                  : undefined
              }
              value={labelOrientation}
              onValueChange={(value) => {
                if (isLabelForcedToAuto) return;
                if (value === "auto" && isLabelAutoDisabled) return;
                if (value === labelOrientation) return;
                handleLabelOrientationChange(value);
              }}
              options={[
                {
                  value: "auto",
                  ariaLabel: "Auto",
                  label: (
                    <span
                      className={isLabelAutoDisabled ? "opacity-40" : undefined}
                    >
                      Auto
                    </span>
                  )
                },
                { value: "top", ariaLabel: "Top", label: "Top" },
                { value: "bottom", ariaLabel: "Bottom", label: "Bottom" }
              ]}
            />
          </div>
        </div>
      </PanelSection>
      {labelPlacementValue === "custom" && (
        <PanelSection label="Label position">
          <div className="flex flex-col gap-3">
            <EditorFieldGrid>
              <div className="flex flex-col gap-1">
                <span className="text-neutral-600 text-xs">Rotation</span>
                <DragNumberInput
                  size={"sm"}
                  min={-360}
                  max={360}
                  value={selectedItem.textProps?.rotation || 0}
                  onChange={handleLabelRotationChangeValue}
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-neutral-600 text-xs">Width</span>
                <DragNumberInput
                  size={"sm"}
                  min={10}
                  max={100}
                  value={selectedItem.textProps?.width || 0}
                  onChange={handleLabelWidthChangeValue}
                />
              </div>
            </EditorFieldGrid>
            <EditorFieldGrid>
              <div className="flex flex-col gap-1">
                <span className="text-neutral-600 text-xs">Top</span>
                <DragNumberInput
                  size={"sm"}
                  value={selectedItem.textProps?.marginTop || 0}
                  onChange={handleLabelMarginTopChangeValue}
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-neutral-600 text-xs">Left</span>
                <DragNumberInput
                  size={"sm"}
                  value={selectedItem.textProps?.marginLeft || 0}
                  onChange={handleLabelMarginLeftChangeValue}
                />
              </div>
            </EditorFieldGrid>
          </div>
        </PanelSection>
      )}
    </>
  );
};
